import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Receipt, CheckCircle2, Clock, XCircle, RefreshCw, Trash2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, money, fmtDateTime, formatError } from "../lib/api";
import { Screen, PageHeader } from "../components/ui-kit";

const BADGE = {
  paid: { label: "Paid", cls: "bg-[#4ADE80]/15 text-[#4ADE80]", Icon: CheckCircle2 },
  submitted: { label: "Awaiting approval", cls: "bg-[#E5A84B]/15 text-[#E5A84B]", Icon: Clock },
  pending: { label: "Awaiting payment", cls: "bg-white/10 text-[#A19DB0]", Icon: Clock },
  rejected: { label: "Not verified", cls: "bg-[#E57373]/15 text-[#ffb4b4]", Icon: XCircle },
};
const FILTERS = [["all", "All"], ["paid", "Paid"], ["awaiting", "Awaiting payment"]];

export default function Orders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [filter, setFilter] = useState("all");
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  useEffect(() => { load(); }, []);

  const shown = useMemo(() => (orders || []).filter((o) => {
    if (filter === "paid") return o.status === "paid";
    if (filter === "awaiting") return o.status === "pending" || o.status === "submitted";
    return true;
  }), [orders, filter]);

  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allShownSelected = shown.length > 0 && shown.every((o) => sel.includes(o.id));
  const toggleAll = () => setSel(allShownSelected ? sel.filter((id) => !shown.some((o) => o.id === id)) : [...new Set([...sel, ...shown.map((o) => o.id)])]);

  const del = async () => {
    if (sel.length === 0) return;
    setBusy(true);
    try {
      await api.post("/orders/delete-batch", { ids: sel });
      toast.success(`Deleted ${sel.length} order${sel.length !== 1 ? "s" : ""}`);
      setSel([]); await load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openOrder = (o) => { if (o.status !== "paid") navigate(`/packages?pay=${o.id}`); };

  return (
    <Screen>
      <PageHeader title="My orders" subtitle="Your purchases and their payment status" />

      <div className="flex items-center gap-2 mb-3">
        <div className="flex gap-2 overflow-x-auto flex-1" data-testid="orders-filters">
          {FILTERS.map(([v, l]) => (
            <button key={v} onClick={() => setFilter(v)} data-testid={`orders-filter-${v}`} className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-medium border transition-colors ${filter === v ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "bg-white/5 border-white/10 text-[#E8E4F0]"}`}>{l}</button>
          ))}
        </div>
        <button onClick={load} data-testid="orders-refresh" className="shrink-0 flex items-center gap-1.5 text-sm text-[#A19DB0] px-2"><RefreshCw size={14} /> Refresh</button>
      </div>

      {shown.length > 0 && (
        <div className="flex items-center justify-between mb-3">
          <label className="flex items-center gap-2 text-sm text-[#A19DB0]">
            <input type="checkbox" checked={allShownSelected} onChange={toggleAll} data-testid="orders-select-all" className="accent-[#D4AF37] w-4 h-4" /> Select all
          </label>
          {sel.length > 0 && (
            <button onClick={del} disabled={busy} data-testid="orders-delete" className="flex items-center gap-1.5 text-sm font-semibold text-[#ffb4b4] bg-[#E57373]/10 border border-[#E57373]/30 rounded-full px-3 py-1.5 disabled:opacity-60">
              <Trash2 size={14} /> Delete ({sel.length})
            </button>
          )}
        </div>
      )}

      {orders === null ? (
        <p className="text-[#A19DB0]">Loading…</p>
      ) : shown.length === 0 ? (
        <div className="text-center py-16 text-[#A19DB0]" data-testid="orders-empty">
          <Receipt size={40} className="mx-auto mb-3 opacity-50" />
          <p>No orders here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {shown.map((o) => {
            const b = BADGE[o.status] || BADGE.pending;
            const checked = sel.includes(o.id);
            const clickable = o.status !== "paid";
            return (
              <div key={o.id} data-testid={`order-${o.id}`} className="rounded-2xl bg-white/5 border border-white/10 p-4 flex gap-3">
                <input type="checkbox" checked={checked} onChange={() => toggle(o.id)} data-testid={`order-select-${o.id}`} className="accent-[#D4AF37] w-4 h-4 mt-1 shrink-0" />
                <button onClick={() => openOrder(o)} className={`flex-1 min-w-0 text-left ${clickable ? "" : "cursor-default"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-display text-lg font-bold tracking-wide text-[#D4AF37]">{o.reference}</span>
                    <span data-testid={`order-status-${o.id}`} className={`inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2.5 py-1 ${b.cls}`}><b.Icon size={12} /> {b.label}</span>
                  </div>
                  <p className="text-[#A19DB0] text-xs mt-1">{fmtDateTime(o.created_at)} · {o.items?.length || 0} item(s){o.method ? ` · ${o.method}` : ""}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(o.items || []).map((it, i) => (
                      <span key={`${it.package_id || "item"}-${i}`} className="text-xs bg-white/5 rounded-full px-2 py-1 text-[#E8E4F0]">{it.name}{it.option ? ` · ${it.option}` : ""}</span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
                    <span className="text-[#A19DB0] text-sm">Total</span>
                    <span className="font-semibold text-[#D4AF37] flex items-center gap-1">{money(o.total)}{clickable && <><span className="text-[#A19DB0] text-xs font-normal ml-2">Pay now</span><ChevronRight size={14} className="text-[#A19DB0]" /></>}</span>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </Screen>
  );
}
